import { useEffect, useState } from "react";
import { Copy, Code2, Download } from "lucide-react";
import { toast } from "sonner";
import { StatusBanner } from "@/components/StatusBanner";
import { IdentityQRPng } from "@/components/identity/IdentityQR";
import { useProfileTheme } from "@/features/profiles/hooks/useProfileTheme";
import { ThemeLoadState } from "@/features/profiles/components/ProfileThemeEditor";
import { ensureOwnObject } from "@/features/identity/api/mediaApi";
import { buildSignatureHtml, buildSignatureText } from "@/features/assets/signature/buildSignatureHtml";
import { SignaturePreview } from "@/features/assets/signature/SignaturePreview";
import { AssetHeader, downloadBlob } from "@/features/assets/assetParts";

function useHostedQr(slug, dataUrl) {
  const [qr, setQr] = useState({ url: null, error: null });
  useEffect(() => {
    if (!slug || !dataUrl) return;
    let active = true;
    ensureOwnObject(`signature/qr-${slug}.png`, () => fetch(dataUrl).then((r) => r.blob()), "image/png").then(
      (url) => active && setQr({ url, error: null }),
      () => active && setQr({ url: null, error: "We couldn't prepare your QR image. The signature works without it." }),
    );
    return () => {
      active = false;
    };
  }, [slug, dataUrl]);
  return qr;
}

const HOW_TO = [
  ["Gmail", "Settings → See all settings → General → Signature → Create new, then paste."],
  ["Outlook", "Settings → Mail → Compose and reply → Email signature, then paste."],
  ["Apple Mail", "Mail → Settings → Signatures → +, untick “Always match my default message font”, then paste."],
];

export default function SignaturePage() {
  const t = useProfileTheme();
  const [qrData, setQrData] = useState(null);
  const qr = useHostedQr(t.slug, qrData);
  if (t.load.status !== "ready") return <ThemeLoadState t={t} />;

  const html = buildSignatureHtml(t.identity, { qrSrc: qr.url });
  const text = buildSignatureText(t.identity);
  const copyRich = async () => {
    try {
      await navigator.clipboard.write([new ClipboardItem({ "text/html": new Blob([html], { type: "text/html" }), "text/plain": new Blob([text], { type: "text/plain" }) })]);
      toast.success("Signature copied");
    } catch {
      navigator.clipboard?.writeText(text).then(() => toast.success("Copied as plain text"), () => toast.error("Couldn't copy"));
    }
  };
  const copyHtml = () => navigator.clipboard?.writeText(html).then(() => toast.success("HTML copied"), () => toast.error("Couldn't copy"));
  const download = () => downloadBlob(new Blob([`<!doctype html><html><head><meta charset="utf-8"><title>Email signature</title></head><body>${html}</body></html>`], { type: "text/html" }), "metworth-signature.html");

  return (
    <div data-testid="signature-page" className="mw-rise space-y-8">
      <IdentityQRPng value={t.identity.qrValue} onReady={setQrData} />
      <AssetHeader eyebrow="Email Signature" title="Your email signature." description="Built from your identity. Copy it into Gmail, Outlook or Apple Mail." />
      {t.load.pp?.status !== "published" && <StatusBanner testId="signature-not-live">Your profile link goes live after activation.</StatusBanner>}
      {qr.error && <StatusBanner testId="signature-qr-error">{qr.error}</StatusBanner>}
      <SignaturePreview html={html} from={t.identity.email || "you@company.com"} />
      <div className="flex flex-wrap gap-3">
        <button type="button" onClick={copyRich} data-testid="signature-copy-button" className="mw-btn mw-btn-primary"><Copy className="h-4 w-4" /> Copy signature</button>
        <button type="button" onClick={copyHtml} data-testid="signature-copy-html-button" className="mw-btn mw-btn-ghost"><Code2 className="h-4 w-4" /> Copy HTML</button>
        <button type="button" onClick={download} data-testid="signature-download-button" className="mw-btn mw-btn-ghost"><Download className="h-4 w-4" /> Download .html</button>
      </div>
      <section className="rounded-lg border border-border bg-card p-5 sm:p-6">
        <h2 className="font-serif text-xl tracking-tight">How to add it</h2>
        <dl className="mt-4 space-y-3 text-sm">
          {HOW_TO.map(([k, v]) => <div key={k}><dt className="font-medium">{k}</dt><dd className="text-muted-foreground">{v}</dd></div>)}
        </dl>
      </section>
    </div>
  );
}
